"use client";

import { Box, Container, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { useLocale } from "next-intl";
import { NextLink } from "@/_components/ui/NextLink";
import { IoChevronForward, IoShieldCheckmark } from "react-icons/io5";
import { HELP_CENTER_IDS } from "../_constants/help-center.ids";

interface PolicyLayoutProps {
  title: string;
  lastUpdated: string;
  children: React.ReactNode;
}

export function PolicyLayout({
  title,
  lastUpdated,
  children,
}: PolicyLayoutProps) {
  const locale = useLocale();

  return (
    <>
      <Box
        id={HELP_CENTER_IDS.policyLayout.hero}
        pb={{ base: 12, md: 20 }}
        pt={{ base: "2rem", md: "3.25rem" }}
        bg="linear-gradient(180deg, rgba(99,91,255,0.05) 0%, rgba(250,248,245,0) 100%)"
      >
        <Container
          id={HELP_CENTER_IDS.policyLayout.heroContainer}
          maxW="5xl"
          px={{ base: 5, md: 6 }}
        >
          <HStack id={HELP_CENTER_IDS.policyLayout.breadcrumb} gap={1.5} mb={6}>
            <NextLink
              id={HELP_CENTER_IDS.policyLayout.linkHome}
              href={`/${locale}/help-center/policy`}
              fontSize="xs"
              color="neutral.400"
              _hover={{ color: "primary.500" }}
              transition="color 0.2s"
            >
              Help Center
            </NextLink>
            <Icon as={IoChevronForward} boxSize={3} color="neutral.300" />
            <Text
              id={HELP_CENTER_IDS.policyLayout.breadcrumbTitle}
              fontSize="xs"
              color="neutral.500"
              fontWeight="medium"
            >
              {title}
            </Text>
          </HStack>

          <Flex
            id={HELP_CENTER_IDS.policyLayout.heroContent}
            direction="column"
            gap={4}
          >
            <Text
              id={HELP_CENTER_IDS.policyLayout.title}
              as="h1"
              fontFamily="heading"
              fontSize={{ base: "2xl", md: "4xl" }}
              fontWeight="bold"
              color="neutral.900"
              letterSpacing="-0.5px"
              lineHeight={1.1}
            >
              {title}
            </Text>
            <HStack id={HELP_CENTER_IDS.policyLayout.lastUpdated} gap={2}>
              <Icon as={IoShieldCheckmark} boxSize={4} color="primary.500" />
              <Text
                fontSize="xs"
                color="neutral.400"
                fontWeight="medium"
                letterSpacing="0.02em"
              >
                {lastUpdated}
              </Text>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Container
        id={HELP_CENTER_IDS.policyLayout.contentContainer}
        maxW="5xl"
        px={{ base: 5, md: 6 }}
      >
        <Box id={HELP_CENTER_IDS.policyLayout.content}>{children}</Box>
      </Container>
    </>
  );
}
